/**
 * Exportacion a Excel: capa delgada sobre la libreria.
 *
 * Toda la logica de que va en cada hoja vive en excel-sheets.ts, que es codigo
 * puro con pruebas. Aqui solo se lee la base de datos y se traduce el resultado
 * al formato que espera write-excel-file.
 *
 * ESTE ARCHIVO NO ES UN RESPALDO. El respaldo oficial y restaurable es el JSON
 * de backup.ts. El Excel es una representacion para analizar los datos fuera de
 * la aplicacion.
 */

// Solo los tipos se importan de forma directa: `import type` desaparece al
// compilar y no arrastra la libreria al paquete principal.
import type { Sheet, Row, CellObject } from 'write-excel-file/browser';
import { db } from './schema';
import { TABLAS, escribirTabla, type MapaTablas } from './types';
import { tablasVacias } from './backup-format';
import { construirHojas, type HojaExcel, type ValorCelda } from './excel-sheets';

export { nombreArchivoExcel, HOJAS_PENDIENTES } from './excel-sheets';

/** Lee las tablas necesarias dentro de una transaccion, para una foto consistente. */
async function leerDatos(): Promise<MapaTablas> {
  const datos = tablasVacias();
  await db.transaction('r', db.tables, async () => {
    for (const tabla of TABLAS) {
      const registros = await db.table(tabla).toArray();
      escribirTabla(datos, tabla, registros);
    }
  });
  return datos;
}

/** Traduce una celda propia al formato de la libreria. */
function celda(valor: ValorCelda, formato: string | null | undefined): CellObject {
  if (valor === null) {
    return { value: undefined, type: String };
  }

  if (typeof valor === 'number') {
    const base: CellObject = { value: valor, type: Number };
    if (formato !== null && formato !== undefined) base.format = formato;
    return base;
  }

  return { value: valor, type: String };
}

function encabezado(titulo: string): CellObject {
  return {
    value: titulo,
    type: String,
    fontWeight: 'bold',
    backgroundColor: '#f4efe7',
    borderStyle: 'thin',
    borderColor: '#cdc2ae',
    wrap: true,
  };
}

/** Traduce una hoja propia al formato de la libreria. */
function traducirHoja(hoja: HojaExcel): Sheet<never> {
  const filas: Row[] = [hoja.columnas.map((c) => encabezado(c.titulo))];

  for (const fila of hoja.filas) {
    filas.push(
      hoja.columnas.map((columna, indice) => celda(fila[indice] ?? null, columna.formato)),
    );
  }

  // Con la hoja vacia se explica por que, en lugar de dejarla en blanco.
  if (hoja.filas.length === 0 && hoja.notaVacia !== undefined) {
    filas.push([{ value: hoja.notaVacia, type: String, fontStyle: 'italic' }]);
  }

  return {
    sheet: hoja.nombre,
    data: filas,
    columns: hoja.columnas.map((c) => ({ width: c.ancho })),
    // Deja fija la fila de encabezados al desplazarse.
    stickyRowsCount: 1,
  } as Sheet<never>;
}

/**
 * Genera el archivo de Excel con las hojas disponibles.
 * Devuelve un Blob listo para descargar o compartir.
 *
 * La libreria se carga BAJO DEMANDA con un import dinamico, no al arrancar.
 * Pesa unos 100 KB y solo hace falta al exportar, asi que dejarla fuera del
 * paquete principal acelera la apertura de la aplicacion.
 *
 * El modo sin conexion no se afecta: el Service Worker precarga todos los
 * fragmentos de codigo, incluido este, durante la instalacion.
 */
export async function exportarExcel(): Promise<Blob> {
  const [{ default: writeXlsxFile }, datos] = await Promise.all([
    import('write-excel-file/browser'),
    leerDatos(),
  ]);
  const hojas = construirHojas(datos).map(traducirHoja);
  return writeXlsxFile(hojas).toBlob();
}

/** Conteo de filas por hoja, para mostrarlo antes de exportar. */
export async function resumenExcel(): Promise<Array<{ nombre: string; filas: number }>> {
  const datos = await leerDatos();
  return construirHojas(datos).map((h) => ({ nombre: h.nombre, filas: h.filas.length }));
}
